// Implements: F-MCK-006, F-UIX-014, F-UIX-007, F-UIX-009
/**
 * The mockup's name in the editor's topbar (UX-05, the mockup's `.crumb-name`): an input that looks
 * like text and is edited in place by whoever may rename (`your_role` owner or editor, the server's
 * matrix); a viewer sees the name as text.
 *
 * Enter or leaving the field saves the trimmed name, once, and only when it changed; Escape puts the
 * server's name back. A blank or too-long name is refused here without asking the server. A save is
 * not optimistic: the field shows the server's name once it answers, the new one or, after a
 * refusal (read out as a notice), the old one. The write is never retried (`retry: false`). An
 * answer that arrives after the container was left behind changes nothing on screen.
 */

import { useEffect, useRef, useState } from "react";
import type { ReactElement } from "react";

import { useTranslation } from "../../../shared/i18n";
import { useToast } from "../../../shared/ui/molecules/ToastProvider";
import { userMessage } from "../../../shared/ui/molecules/userMessage";
import { codePoints } from "../components/tagDraft";
import { useMockupActions } from "../hooks/useMockupActions";
import { useMockupDetail } from "../hooks/useMockupDetail";
import { MAX_NAME_LENGTH } from "./MockupDetailContainer";
import styles from "./MockupNameContainer.module.css";

const EDITOR_ROLES: ReadonlySet<string> = new Set(["owner", "editor"]);

export interface MockupNameContainerProps {
  readonly mockupId: string;
}

export function MockupNameContainer({ mockupId }: MockupNameContainerProps): ReactElement | null {
  const { t } = useTranslation();
  const mockup = useMockupDetail(mockupId);
  const { rename } = useMockupActions();
  const { push } = useToast();
  // `null` while the field shows the server's name; the typed text otherwise.
  const [draft, setDraft] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const inFlight = useRef(false);
  const alive = useRef(true);
  // The field had the focus when the editing went away: the name takes it, never the page.
  const fieldFocused = useRef(false);
  const text = useRef<HTMLSpanElement | null>(null);
  const editable = mockup.data !== undefined && EDITOR_ROLES.has(mockup.data.your_role);
  useEffect(() => {
    const lost = document.activeElement === null || document.activeElement === document.body;
    if (!editable && fieldFocused.current && lost) {
      fieldFocused.current = false;
      text.current?.focus();
    }
  }, [editable]);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  if (mockup.data === undefined) return null;
  const serverName = mockup.data.name;
  if (!editable) {
    return (
      <span ref={text} className={styles.text} tabIndex={-1}>
        {serverName}
      </span>
    );
  }

  const commit = async (): Promise<void> => {
    if (draft === null || inFlight.current) return;
    const name = draft.trim();
    if (name === serverName) {
      setDraft(null);
      return;
    }
    if (name.length === 0 || codePoints(name) > MAX_NAME_LENGTH) {
      setDraft(null);
      push({
        tone: "danger",
        message:
          name.length === 0 ? t("mockup.detail.nameEmpty") : t("mockup.detail.nameTooLong", { max: MAX_NAME_LENGTH }),
      });
      return;
    }
    inFlight.current = true;
    setSaving(true);
    const refusal = await rename(mockupId, name);
    inFlight.current = false;
    if (!alive.current) return;
    setSaving(false);
    setDraft(null);
    push(
      refusal === null
        ? { tone: "success", message: t("mockup.detail.renamed") }
        : { tone: "danger", message: userMessage(refusal, t("mockup.detail.renameFailed")) },
    );
  };

  return (
    <input
      className={styles.name}
      aria-label={t("editor.name.label")}
      value={draft ?? serverName}
      size={Math.min(28, Math.max(10, (draft ?? serverName).length))}
      readOnly={saving}
      onChange={(event) => {
        setDraft(event.target.value);
      }}
      onKeyDown={(event) => {
        if (event.key === "Enter") {
          event.preventDefault();
          void commit();
        } else if (event.key === "Escape") {
          event.preventDefault();
          setDraft(null);
        }
      }}
      onFocus={() => {
        fieldFocused.current = true;
      }}
      onBlur={(event) => {
        // A blur to nowhere is the field going away (or the window): the name may still need the focus.
        fieldFocused.current = event.relatedTarget === null;
        void commit();
      }}
    />
  );
}
